import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { useToast } from '@/components/ui/use-toast';
import { Installer } from '../components/installers/InstallersTab';
import { logInstallerActivity } from '@/utils/activityLogger';

export function useInstallers() {
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchInstallers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('installer_partners')
        .select(`
          id,
          company_name,
          is_active,
          created_at,
          updated_at,
          street,
          city,
          state,
          zip_code,
          county,
          user_id,
          email,
          phone,
          cost_ppw,
          partner_type,
          price_ppw,
          internal_pm_manager:users!internal_pm_manager_id(id, first_name, last_name),
          installer_pm_manager:users!installer_pm_manager_id(id, first_name, last_name),
          biz_dev_rep:users!biz_dev_rep_id(id, first_name, last_name)
        `)
        .order('company_name');

      if (error) {
        throw new Error('Failed to fetch installers: ' + error.message);
      }

      // People per company. A failure here only loses the count, never the list.
      const peopleCount = new Map<string, number>();
      const { data: links } = await supabase.from('user_installer_partners').select('installer_partner_id');
      for (const l of links ?? []) {
        peopleCount.set(l.installer_partner_id, (peopleCount.get(l.installer_partner_id) ?? 0) + 1);
      }

      const formattedInstallers: Installer[] = data.map((installer: any) => ({
        id: installer.id,
        name: installer.company_name,
        address: installer.street || '',
        city: installer.city || '',
        state: installer.state || '',
        zip_code: installer.zip_code || '',
        county: installer.county || null,
        is_active: installer.is_active,
        created_at: installer.created_at,
        updated_at: installer.updated_at,
        user_id: installer.user_id || null,
        email: installer.email || null,
        phone: installer.phone || null,
        cost_ppw: installer.cost_ppw !== undefined ? installer.cost_ppw : null,
        partner_type: installer.partner_type === 'EPC' ? 'EPC' : 'INSTALLER',
        price_ppw: installer.price_ppw ?? null,
        people_count: peopleCount.get(installer.id) ?? 0,
        internal_pm_manager_id: installer.internal_pm_manager?.id || null,
        internal_pm_manager_name: installer.internal_pm_manager
          ? `${installer.internal_pm_manager.first_name} ${installer.internal_pm_manager.last_name}`
          : null,
        installer_pm_manager_id: installer.installer_pm_manager?.id || null,
        installer_pm_manager_name: installer.installer_pm_manager
          ? `${installer.installer_pm_manager.first_name} ${installer.installer_pm_manager.last_name}`
          : null,
        biz_dev_rep_id: installer.biz_dev_rep?.id || null,
        biz_dev_rep_name: installer.biz_dev_rep
          ? `${installer.biz_dev_rep.first_name} ${installer.biz_dev_rep.last_name}`
          : null,
      }));

      setInstallers(formattedInstallers);
    } catch (error: any) {
      console.error('Error fetching installers:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch installers.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createInstaller = async (installerData: Omit<Installer, 'id' | 'created_at' | 'updated_at'>): Promise<string> => {
    try {
      const tenantId = await getCurrentTenantId();
      
      const { data, error } = await supabase
        .from('installer_partners')
        .insert({
          company_name: installerData.name,
          street: installerData.address || '',
          city: installerData.city || '',
          state: installerData.state || '',
          zip_code: installerData.zip_code || '',
          county: installerData.county || null,
          is_active: installerData.is_active !== undefined ? installerData.is_active : true,
          user_id: installerData.user_id || null,
          internal_pm_manager_id: installerData.internal_pm_manager_id || null,
          installer_pm_manager_id: installerData.installer_pm_manager_id || null,
          biz_dev_rep_id: installerData.biz_dev_rep_id || null,
          email: installerData.email || null,
          phone: installerData.phone || null,
          cost_ppw: installerData.cost_ppw !== undefined ? installerData.cost_ppw : null,
          partner_type: installerData.partner_type ?? 'INSTALLER',
          price_ppw: installerData.price_ppw ?? null,
          tenant_id: tenantId
        })
        .select('id')
        .single();

      if (error) {
        throw new Error('Failed to create installer: ' + error.message);
      }

      const newInstaller: Installer = {
        ...installerData,
        id: data.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      setInstallers((prev) => [...prev, newInstaller]);
      
      // Log the installer creation activity
      await logInstallerActivity.created(data.id, {
        company_name: installerData.name,
        email: installerData.email,
        phone: installerData.phone
      });
      
      toast({
        title: 'Success',
        description: 'Partner created.',
      });
      return data.id;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateInstaller = async (installerData: Installer): Promise<string> => {
    try {
      if (!installerData.id) {
        throw new Error('Installer ID is required for updates');
      }


      // Get the current tenant ID
      const tenantId = await getCurrentTenantId();

      const updatePayload = {
        company_name: installerData.name,
        street: installerData.address || null,
        city: installerData.city || null,
        state: installerData.state || null,
        zip_code: installerData.zip_code || null,
        county: installerData.county || null,
        is_active: installerData.is_active !== undefined ? installerData.is_active : true,
        user_id: installerData.user_id || null,
        internal_pm_manager_id: installerData.internal_pm_manager_id || null,
        installer_pm_manager_id: installerData.installer_pm_manager_id || null,
        biz_dev_rep_id: installerData.biz_dev_rep_id || null,
        email: installerData.email || null,
        phone: installerData.phone || null,
        cost_ppw: installerData.cost_ppw !== null ? installerData.cost_ppw : null,
        partner_type: installerData.partner_type ?? 'INSTALLER',
        price_ppw: installerData.price_ppw ?? null,
        updated_at: new Date().toISOString()
      };


      const { data, error } = await supabase
        .from('installer_partners')
        .update(updatePayload)
        .eq('id', installerData.id)
        .eq('tenant_id', tenantId)
        .select('id');

      if (error) {
        throw new Error('Failed to update installer: ' + error.message);
      }

      // If data is null or empty, it means no rows were updated
      if (!data || (Array.isArray(data) && data.length === 0)) {
        // Return the original ID since we know it exists (we're updating it)
        const resultId = installerData.id;

        // Verify the installer exists
        const { data: checkData, error: checkError } = await supabase
          .from('installer_partners')
          .select('id')
          .eq('id', installerData.id)
          .single();

        if (checkError || !checkData) {
          throw new Error('Installer not found or no permission to update');
        }

        // The update likely succeeded but didn't return data due to RLS
        setInstallers((prev) =>
          prev.map((installer) =>
            installer.id === resultId ? { ...installerData } : installer
          )
        );

        // Log the installer update activity
        await logInstallerActivity.updated(resultId, {
          company_name: installerData.name,
          email: installerData.email,
          phone: installerData.phone
        });

        toast({
          title: 'Success',
          description: 'Partner updated.',
        });
        return resultId;
      }

      // Handle the case where data is returned
      const resultId = Array.isArray(data) && data.length > 0 ? data[0].id : (data as any)?.id;

      setInstallers((prev) =>
        prev.map((installer) =>
          installer.id === resultId ? { ...installerData } : installer
        )
      );

      // Log the installer update activity
      await logInstallerActivity.updated(resultId, {
        company_name: installerData.name,
        email: installerData.email,
        phone: installerData.phone
      });

      toast({
        title: 'Success',
        description: 'Partner updated.',
      });
      return resultId;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteInstaller = async (installerId: string) => {
    try {
      const { data, error } = await supabase
        .from('installer_partners')
        .delete()
        .eq('id', installerId)
        .select('id'); // Select to confirm deletion

      if (error) {
        throw new Error('Failed to delete installer: ' + error.message);
      }

      setInstallers((prev) => prev.filter((installer) => installer.id !== installerId));
      
      // Log the installer deletion activity
      await logInstallerActivity.deleted(installerId, {
        deleted_installer_id: installerId
      });
      
      toast({
        title: 'Success',
        description: 'Partner deleted.',
      });
    } catch (error: any) {
      console.error('Error deleting installer:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchInstallers();
  }, []);

  return {
    installers,
    isLoading,
    createInstaller,
    updateInstaller,
    deleteInstaller,
  };
}